import { useEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import { IDIOMAS, NOME_DO_IDIOMA, t, trocarIdioma } from '../../i18n/idioma';
import { NOME_DO_JOGO } from '../../marca';
import { Balao, Camera, Estrela, ICONES_RECURSOS } from './icones';
import { arquivo, fontesDaTela, LARGURAS, type Tela } from './imagens';

/**
 * O que as duas landings têm em comum (techspec-landing-fase §06 e a landing aberta, docs/prototipos/landing-aberta-nota.md):
 * os fundos, as seções que apresentam o jogo, a galeria, as redes e o rodapé.
 */
const CELULAR = '(max-width: 900px)';
const PROFISSOES = ['mago', 'guerreiro', 'arqueiro', 'assassino', 'espadachim'] as const;
const PASSOS = [1, 2, 3] as const;

export function useLarguraDosFundos(): number {
  const [celular, setCelular] = useState(() => window.matchMedia(CELULAR).matches);
  useEffect(() => {
    const mq = window.matchMedia(CELULAR);
    const ao = () => setCelular(mq.matches);
    mq.addEventListener('change', ao);
    return () => mq.removeEventListener('change', ao);
  }, []);
  return celular ? LARGURAS.heroi[0] : LARGURAS.heroi[1];
}

/** Só o herói é pré-carregado: é o que aparece na primeira dobra. */
export function usePrecarregarHeroi(largura: number) {
  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.type = 'image/avif';
    link.href = arquivo('heroi', largura, 'avif');
    document.head.appendChild(link);
    return () => link.remove();
  }, [largura]);
}

/** O título da aba enquanto a landing está na tela. */
export function useTituloDaAba(titulo: string) {
  useEffect(() => {
    const antes = document.title;
    document.title = titulo;
    return () => { document.title = antes; };
  }, [titulo]);
}

/** Quem rola é a raiz .lp (o body do jogo é overflow: hidden): o topo escurece depois de 24 px. */
export function useRolou(raiz: RefObject<HTMLDivElement | null>): boolean {
  const [rolou, setRolou] = useState(false);
  useEffect(() => {
    const el = raiz.current;
    if (!el) return;
    const ao = () => setRolou(el.scrollTop > 24);
    el.addEventListener('scroll', ao, { passive: true });
    return () => el.removeEventListener('scroll', ao);
  }, [raiz]);
  return rolou;
}

export function Secao({ id, pedra, centro, sobre, titulo, texto, children, tituloId }: {
  id?: string;
  pedra?: boolean;
  centro?: boolean;
  sobre: string;
  titulo: string;
  tituloId: string;
  texto?: string;
  children?: ReactNode;
}) {
  return (
    <section className={pedra ? 'lp-secao lp-secao--pedra' : 'lp-secao'} id={id} aria-labelledby={tituloId}>
      <div className="lp-envolve">
        <div className={centro ? 'lp-secao__cab lp-secao__cab--centro' : 'lp-secao__cab'}>
          <span className="lp-sobre">{sobre}</span>
          <h2 id={tituloId}>{titulo}</h2>
          {texto ? <p>{texto}</p> : null}
          {pedra && !texto ? <div className="lp-filete"><Estrela /></div> : null}
        </div>
        {children}
      </div>
    </section>
  );
}

function Fonte({ nome, alt, sizes }: { nome: Tela; alt: string; sizes: string }) {
  const [menor] = LARGURAS[nome];
  return (
    <picture>
      {fontesDaTela(nome).map(f => <source key={f.tipo} type={f.tipo} srcSet={f.srcSet} sizes={sizes} />)}
      <img src={arquivo(nome, menor, 'jpg')} alt={alt} decoding="async" />
    </picture>
  );
}

/** As duas telas com o zoom (Esc fecha e devolve o foco a quem abriu). */
export function Galeria() {
  const [aberta, setAberta] = useState<Tela | null>(null);
  const origem = useRef<HTMLButtonElement | null>(null);
  const zoom = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!aberta) return;
    zoom.current?.focus();
    const tecla = (e: KeyboardEvent) => { if (e.key === 'Escape') fechar(); };
    window.addEventListener('keydown', tecla);
    return () => window.removeEventListener('keydown', tecla);
  }, [aberta]);

  const abrir = (nome: Tela, botao: HTMLButtonElement) => {
    origem.current = botao;
    setAberta(nome);
  };
  const fechar = () => {
    setAberta(null);
    origem.current?.focus();
  };

  // O cemitério (HUD antigo) saiu da galeria; os arquivos e os textos ficam para voltar quando houver tela nova.
  const telas: readonly Tela[] = ['minoc', 'destard', 'mina'];
  return (
    <>
      <div className="lp-telas">
        {telas.map(nome => (
          <figure className="lp-tela-jogo" key={nome}>
            <button type="button" aria-label={t(`landing.${nome}.ampliar`)} onClick={e => abrir(nome, e.currentTarget)}>
              <Fonte nome={nome} alt={t(`landing.${nome}.alt`)} sizes="(max-width: 980px) 100vw, 34vw" />
            </button>
            <figcaption>
              <b>{t(`landing.${nome}.nome`)}</b>
              <span>{t(`landing.${nome}.legenda`)}</span>
            </figcaption>
          </figure>
        ))}
      </div>
      <div className="lp-zoom" ref={zoom} hidden={!aberta} role="dialog" aria-modal="true"
        aria-label={t('landing.zoom')} tabIndex={-1} onClick={fechar}>
        {aberta ? <Fonte nome={aberta} alt="" sizes="100vw" /> : null}
      </div>
    </>
  );
}

/** Como se joga, o que você encontra e as telas: a apresentação do jogo, igual nas duas landings. */
export function SecoesDoJogo() {
  const [antesRunas, depoisRunas] = t('landing.r2.texto', { runas: '\u0000' }).split('\u0000');
  return (
    <>
      <Secao id="jogo" tituloId="lp-t-jogo" sobre={t('landing.jogo.sobre')} titulo={t('landing.jogo.titulo')} texto={t('landing.jogo.texto')}>
        <div className="lp-laco">
          {PASSOS.map(n => (
            <div className="lp-passo" key={n}>
              <h3>{t(`landing.passo${n}.titulo`)}</h3>
              <p>{t(`landing.passo${n}.texto`)}</p>
            </div>
          ))}
        </div>
      </Secao>

      <Secao pedra centro tituloId="lp-t-recursos" sobre={t('landing.recursos.sobre')} titulo={t('landing.recursos.titulo')}>
        <div className="lp-recursos">
          {ICONES_RECURSOS.map((icone, i) => (
            <article className="lp-recurso" key={i}>
              {icone}
              <h3>{t(`landing.r${i + 1}.titulo`)}</h3>
              <p>
                {i === 1
                  ? <>{antesRunas}<span className="lp-runas">Kal Vas Flam</span>{depoisRunas}</>
                  : t(`landing.r${i + 1}.texto`)}
              </p>
            </article>
          ))}
        </div>
        <div className="lp-profissoes" aria-label={t('landing.profissoes')}>
          {PROFISSOES.map(p => <span key={p}>{t(`landing.prof.${p}`)}</span>)}
        </div>
      </Secao>

      <Secao id="telas" tituloId="lp-t-telas" sobre={t('landing.telas.sobre')} titulo={t('landing.telas.titulo')} texto={t('landing.telas.texto')}>
        <Galeria />
      </Secao>
    </>
  );
}

/** Discord e Instagram, na chamada final. */
export function Redes({ redes }: { redes: { discord: string; instagram: string; instagramUsuario: string } }) {
  return (
    <div className="lp-redes">
      <a className="lp-rede" href={redes.discord} target="_blank" rel="noopener noreferrer">
        <Balao />
        <span>Discord<br /><small>{t('landing.final.discord')}</small></span>
      </a>
      <a className="lp-rede" href={redes.instagram} target="_blank" rel="noopener noreferrer">
        <Camera />
        <span>Instagram<br /><small>@{redes.instagramUsuario}</small></span>
      </a>
    </div>
  );
}

/** O rodapé: os links de cada landing, a Wiki, as redes e os idiomas. */
export function Rodape({ idioma, redes, children }: {
  idioma: string;
  redes: { discord: string; instagram: string };
  children?: ReactNode;
}) {
  return (
    <footer className="lp-rodape">
      <div className="lp-envolve lp-rodape__in">
        <span>© {new Date().getFullYear()} {NOME_DO_JOGO}</span>
        <nav aria-label={t('landing.rodape')}>
          {children}
          <a href="/wiki">{t('landing.nav.wiki')}</a>
          <a href="/parceiros">{t('landing.rodape.parceiros')}</a>
          <a href="/privacy.html">{t('legal.privacidade')}</a>
          <a href="/term.html">{t('legal.termos')}</a>
          <a href={redes.discord} target="_blank" rel="noopener noreferrer">Discord</a>
          <a href={redes.instagram} target="_blank" rel="noopener noreferrer">Instagram</a>
        </nav>
        <div className="lp-idiomas" role="group" aria-label={t('landing.idioma')}>
          {IDIOMAS.map(id => (
            <button key={id} type="button" aria-pressed={id === idioma} onClick={() => { void trocarIdioma(id); }}>
              {NOME_DO_IDIOMA[id]}
            </button>
          ))}
        </div>
      </div>
    </footer>
  );
}
